import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Heart, Users, Vote, MessageSquare, Sparkles,
  ChevronLeft, ChevronRight, Star, Calendar, MapPin, Megaphone,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FourStars, StarsDivider } from "../components/FourStars";
import Logo from "../components/Logo";
import { api } from "../lib/api";

// Real client-provided photos (official A.F.B. delivery)
const PRESIDENT_PHOTOS = {
  // Latest delivery — official premium shots
  diasporaEvent: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/un3bofmf_ABRAH%20ODIANCE.JPG",
  presidentialMission: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/t1xtyy1k_WhatsApp%20Image%202026-04-30%20at%2017.49.56.jpeg",
  // Existing banner/photo set
  hero: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/3cn3sivo_BANNIERE%203.JPG",
  banniere1: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/scki92mu_BANNIERE1.JPG",
  banniere2: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/z7yiome8_BANNIERE2.JPG",
  banniere5: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/nplw4r61_BANNIERE%205.JPG",
  bureau: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/w32wtzn3_BUREAU%20AFB.JPG",
  abrahamPresident: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/krgqysuz_ABRAHAM%20%2B%20PRESIDENT.JPG",
  accueil: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/7zzs6fwf_ACCUEIL%20.JPG",
  audience: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/4y1r0u17_ABRA%20%2B%20PRESIDENT.JPG",
  ministre: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/3p1c0gg5_ABRAHAM%20%2B%20MINISTRES.JPG",
  international: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/0o7lfgvn_ABRAH%20%2B%20SUMOI.JPG",
  ministre2: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/psr2p0uv_ABRAH%20MINISTRE.JPG",
  audience2: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/fkk77b8w_ABRAH%20ODIANCE.JPG",
  // Reality of Congo — war in the East
  guerreFlee: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/s87wec5a_gueere.jpg",
  guerreCamp: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/pi4p4ij2_guerre.webp",
  guerreExode: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/zthqc74f_guerres%20l%28est.jpg",
  // Real construction sites in DRC
  chantierRdc: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/qr2o3b8y_chantier%20%20rdc.jpeg",
  chantierRoute: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/geivyvzy_chantier%20route.webp",
};

const HERO_SLIDES = [
  {
    image: PRESIDENT_PHOTOS.diasporaEvent,
    subtitle:
      "La diaspora congolaise debout aux côtés du Chef de l'État. Une mobilisation patriotique pour soutenir la vision présidentielle.",
    focal: "center 30%",
  },
  {
    image: PRESIDENT_PHOTOS.abrahamPresident,
    subtitle:
      "Son Excellence Félix-Antoine Tshisekedi Tshilombo reçoit en audience la délégation des Amis de Fatshi Béton.",
    focal: "center 25%",
  },
  {
    image: PRESIDENT_PHOTOS.banniere5,
    subtitle:
      "Un peuple engagé. Une vision affirmée. Un avenir en construction pour la République Démocratique du Congo.",
    focal: "center 35%",
  },
  {
    image: PRESIDENT_PHOTOS.audience,
    subtitle:
      "Le Chef de l'État, garant de l'unité nationale, à l'écoute de ceux qui portent le Congo dans leur cœur.",
    focal: "center 30%",
  },
  {
    image: PRESIDENT_PHOTOS.banniere1,
    subtitle:
      "De Kinshasa à la diaspora mondiale : un mouvement structuré au service du Congo de demain.",
    focal: "center 40%",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/r8s64thf_ABRAH%20%2B%20PRESIDENT%20.JPG",
    subtitle:
      "Une RDC qui se relève. Une RDC qui avance. Une RDC fière de son Président.",
    focal: "center 25%",
  },
  {
    image: PRESIDENT_PHOTOS.hero,
    subtitle:
      "Pour un Congo Béton : unité, progrès et travail. Les Amis de Fatshi Béton portent l'élan d'un peuple uni derrière son Président.",
    focal: "center 25%",
  },
  {
    image: PRESIDENT_PHOTOS.presidentialMission,
    subtitle:
      "Un Congo qui rayonne. Sous l'impulsion du Président Félix Tshisekedi, la RDC s'impose sur la scène internationale.",
    focal: "center 35%",
  },
];

const SERVICES = [
  { icon: Heart, title: "Dons & Cotisations", text: "Stripe & Mobile Money RDC. Soutenez les actions sur le terrain.", to: "/dons", testid: "service-dons" },
  { icon: Users, title: "Espace Membre", text: "Cotisations, profil, communauté A.F.B.", to: "/dashboard", testid: "service-membre" },
  { icon: Vote, title: "Sondages & Élections", text: "Votre voix oriente les décisions du mouvement.", to: "/sondages", testid: "service-sondages" },
  { icon: MessageSquare, title: "Messagerie Interne", text: "Échangez en confidentialité avec les autres membres.", to: "/dashboard", testid: "service-messagerie" },
  { icon: Sparkles, title: "Assistant IA", text: "Un assistant intelligent en français, 24/7.", to: "/", testid: "service-ia" },
  { icon: Megaphone, title: "Mobilisation Terrain", text: "Rejoignez les sections locales et la diaspora.", to: "/register", testid: "service-mobilisation" },
];

export default function HomePage() {
  const [slide, setSlide] = useState(0);
  const [news, setNews] = useState([]);
  const [actions, setActions] = useState([]);
  const [cms, setCms] = useState({});

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data.slice(0, 6))).catch(() => {});
    api.get("/actions").then((r) => setActions(r.data.slice(0, 6))).catch(() => {});
    api.get("/cms/homepage").then((r) => setCms(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — cinematic premium institutional */}
      <section className="relative min-h-[100vh] overflow-hidden bg-[#020409]" data-testid="hero-slider">
        {/* Slides with Ken Burns effect */}
        {HERO_SLIDES.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${i === slide ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <div
              className={`absolute inset-0 ${i === slide ? "hero-kenburns" : ""}`}
              style={{
                backgroundImage: `url(${s.image})`,
                backgroundSize: "cover",
                backgroundPosition: s.focal || "center",
              }}
            />
            {/* Layered cinematic overlay: blue + black + gold rim */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(2,4,9,0.65) 0%, rgba(2,4,9,0.35) 35%, rgba(0,30,82,0.55) 70%, rgba(2,4,9,0.92) 100%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at 50% 60%, rgba(0,82,204,0.35) 0%, rgba(2,4,9,0.85) 70%)",
              }}
            />
            {/* Subtle vignette */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.65) 100%)",
              }}
            />
            <div className="absolute inset-0 stars-pattern opacity-30" />
            {/* Gold rim light from top */}
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.7) 25%, rgba(212,175,55,1) 50%, rgba(212,175,55,0.7) 75%, transparent 100%)",
                boxShadow: "0 0 30px rgba(212,175,55,0.5)",
              }}
            />
          </div>
        ))}

        {/* Decorative gold corners */}
        <div className="pointer-events-none absolute top-24 left-8 w-28 h-28 border-l border-t border-[#D4AF37]/50 hidden md:block">
          <div className="absolute -top-px -left-px w-10 h-px bg-[#D4AF37]" />
          <div className="absolute -top-px -left-px w-px h-10 bg-[#D4AF37]" />
        </div>
        <div className="pointer-events-none absolute bottom-28 right-8 w-28 h-28 border-r border-b border-[#D4AF37]/50 hidden md:block">
          <div className="absolute -bottom-px -right-px w-10 h-px bg-[#D4AF37]" />
          <div className="absolute -bottom-px -right-px w-px h-10 bg-[#D4AF37]" />
        </div>

        <div className="relative z-10 min-h-[100vh] max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-32 flex items-center justify-center">
          <motion.div
            key={slide}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-center text-white w-full"
          >
            {/* Big centered logo USN-style */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="flex justify-center mb-8 relative"
            >
              <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
                <div className="w-44 h-44 rounded-full bg-[#D4AF37]/20 blur-3xl" />
              </div>
              <div className="relative">
                <Logo variant="badge" size={140} />
              </div>
            </motion.div>

            {/* GIANT NAME — USN style: bold, all caps, white */}
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.35 }}
              className="font-display font-black leading-[0.95] tracking-[-0.02em] text-white hero-title-glow"
              style={{ fontSize: "clamp(1.75rem, 7vw, 5.8rem)" }}
            >
              {cms.hero_title || "LES AMIS DE FATSHI BÉTON"}
            </motion.h1>

            {/* SECTION DIASPORA — gold caption USN style */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="font-display font-bold text-[#D4AF37] mt-6"
              style={{ fontSize: "clamp(0.95rem, 2.4vw, 1.7rem)", letterSpacing: "0.18em" }}
            >
              {cms.hero_subtitle || "SECTION DIASPORA"}
            </motion.div>

            {/* DRC flag separator (USN style) */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0.6 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="flex items-center justify-center gap-2 mt-7"
            >
              <span className="w-12 h-1 bg-[#0052CC]" />
              <span className="w-12 h-1 bg-[#D4AF37]" />
              <span className="w-12 h-1 bg-[#CE1126]" />
            </motion.div>

            {/* Tagline (dynamic per slide — supportive narrative) */}
            <motion.p
              key={`tagline-${slide}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-7 text-base md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]"
              data-testid="hero-tagline"
            >
              {HERO_SLIDES[slide].subtitle || cms.hero_tagline || "Pour un Congo fort, uni et prospère"}
            </motion.p>

            {/* Italic gold quote (USN style) */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15 }}
              className="mt-4 italic text-[#D4AF37] text-sm md:text-lg max-w-2xl mx-auto"
            >
              {cms.hero_devise || "« UNITÉ — PROGRÈS — TRAVAIL »"}
            </motion.p>

            {/* CTAs USN style */}
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 1.3 }}
              className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-10 sm:mt-12 px-4 sm:px-0 btn-stack-mobile"
              data-testid="hero-ctas"
            >
              <Button asChild size="lg" className="bg-white hover:bg-white/95 text-[#0A1F44] rounded-full px-9 py-7 text-[14px] font-bold tracking-wider uppercase btn-lift" data-testid="hero-discover-btn">
                <Link to={cms.hero_cta_primary_link || "/organisation"}>{cms.hero_cta_primary_label || "Découvrir A.F.B."} <ArrowRight className="ml-2" size={17} /></Link>
              </Button>
              <Button asChild size="lg" className="border-2 border-[#D4AF37] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1F44] rounded-full px-9 py-7 text-[14px] font-bold tracking-wider uppercase transition-all" data-testid="hero-contact-btn">
                <Link to={cms.hero_cta_secondary_link || "/contact"}>{cms.hero_cta_secondary_label || "Nous contacter"}</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* slider controls */}
        <div className="absolute bottom-8 left-0 right-0 z-10 flex items-center justify-center gap-3" data-testid="hero-dots">
          <button onClick={() => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur border border-white/15" aria-label="Précédent" data-testid="hero-prev">
            <ChevronLeft size={18} />
          </button>
          {HERO_SLIDES.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-12 bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.7)]" : "w-3 bg-white/40"}`} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`} />
          ))}
          <button onClick={() => setSlide((s) => (s + 1) % HERO_SLIDES.length)} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur border border-white/15" aria-label="Suivant" data-testid="hero-next">
            <ChevronRight size={18} />
          </button>
        </div>
      </section>

      {/* BANDEAU IDENTITAIRE */}
      <section className="bg-[#0A0F1A] text-white py-10 border-t-4 border-[#D4AF37]" data-testid="identity-band">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Logo variant="badge" size={64} />
            <div>
              <div className="font-display text-xl md:text-2xl">LES AMIS DE FATSHI BÉTON</div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mt-1">A.F.B. · Mouvement national & diaspora</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6 md:gap-10 text-sm">
            <div className="flex items-center gap-2"><MapPin size={15} className="text-[#D4AF37]" /> Présence dans les 26 provinces</div>
            <div className="flex items-center gap-2"><Star size={14} fill="#D4AF37" stroke="#D4AF37" /> Diaspora active 5 continents</div>
          </div>
        </div>
      </section>

      {/* VISION + LE MOUVEMENT (avec photos terrain) */}
      <section className="py-20 md:py-28" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">{cms.vision_overline || "★ ★ ★ ★   Notre vision"}</div>
            <h2 className="font-display text-3xl md:text-5xl text-[#0A0F1A] mt-3 leading-[1.05]">
              {cms.vision_title ? cms.vision_title : (<>Un mouvement national au service d'un <span className="text-[#0052CC]">Congo Béton</span>.</>)}
            </h2>
            <p className="text-[#475569] mt-6 leading-relaxed text-base md:text-lg">
              {cms.vision_paragraph || "Les Amis de Fatshi Béton (A.F.B.) forment une communauté patriotique structurée — citoyens et diaspora — unie autour de Son Excellence Monsieur le Président de la République, Félix-Antoine Tshisekedi Tshilombo. Depuis 2019, sous son leadership, la République Démocratique du Congo connaît une transformation historique : gratuité de l'enseignement de base, lancement de la Couverture Santé Universelle, Programme de Développement Local des 145 Territoires, modernisation des infrastructures, présidence de l'Union Africaine et adhésion à la Communauté Est-Africaine. Nous soutenons cette vision et mobilisons toutes les énergies pour bâtir un Congo solidaire, pacifié et prospère."}
            </p>
            <div className="grid grid-cols-3 gap-4 mt-9">
              <div className="text-center p-5 rounded-lg border border-[#0052CC]/15 bg-[#0052CC]/5">
                <div className="font-display text-3xl text-[#0052CC] font-bold">{cms.pillar_1_label || "UNITÉ"}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-1">Premier pilier</div>
              </div>
              <div className="text-center p-5 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10">
                <div className="font-display text-3xl text-[#B5952F] font-bold">{cms.pillar_2_label || "PROGRÈS"}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-1">Deuxième pilier</div>
              </div>
              <div className="text-center p-5 rounded-lg border border-[#CE1126]/20 bg-[#CE1126]/5">
                <div className="font-display text-3xl text-[#CE1126] font-bold">{cms.pillar_3_label || "TRAVAIL"}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-1">Troisième pilier</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              src={PRESIDENT_PHOTOS.abrahamPresident}
              alt="A.F.B. en audience avec le Président Félix Tshisekedi"
              className="rounded-xl shadow-lg w-full aspect-[3/4] object-cover col-span-1 row-span-2"
              data-testid="vision-img-1"
            />
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              src={PRESIDENT_PHOTOS.audience}
              alt="A.F.B. reçus par le Président de la République"
              className="rounded-xl shadow-lg w-full aspect-[4/3] object-cover"
              data-testid="vision-img-2"
            />
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              src="https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/r8s64thf_ABRAH%20%2B%20PRESIDENT%20.JPG"
              alt="Rencontre officielle avec le Chef de l'État"
              className="rounded-xl shadow-lg w-full aspect-[4/3] object-cover"
              data-testid="vision-img-3"
            />
          </div>
        </div>
      </section>

      <StarsDivider />

      {/* BILAN DU PRÉSIDENT — exploits chiffrés */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-[#0A1F44] via-[#0A1F44] to-[#0052CC] text-white overflow-hidden" data-testid="bilan-president">
        <div className="absolute inset-0 stars-pattern opacity-15" />
        <div
          className="absolute inset-y-0 right-0 w-1/2 opacity-35 hidden lg:block"
          style={{
            backgroundImage: `url(${PRESIDENT_PHOTOS.chantierRoute})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to left, black, transparent)",
            WebkitMaskImage: "linear-gradient(to left, black, transparent)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Bilan présidentiel — RDC en marche</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.05]">
              Six années de <span className="text-[#D4AF37]">transformation historique</span><br />
              sous le leadership du Président <span className="text-[#D4AF37]">Félix Tshisekedi</span>.
            </h2>
            <p className="text-white/80 mt-6 text-base md:text-lg leading-relaxed max-w-2xl">
              De la gratuité de l'enseignement à la Couverture Santé Universelle, de la diplomatie panafricaine à la modernisation des infrastructures, le Chef de l'État écrit une page nouvelle de l'histoire du Congo. Les A.F.B. soutiennent et amplifient cet élan.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-12" data-testid="bilan-stats">
            {[
              { v: "+4M", l: "Enfants scolarisés grâce à la gratuité" },
              { v: "145", l: "Territoires couverts par le PDL" },
              { v: "26/26", l: "Provinces mobilisées" },
              { v: "UA 2021", l: "Présidence de l'Union Africaine" },
              { v: "EAC", l: "Adhésion à la Communauté Est-Africaine" },
              { v: "CSU", l: "Couverture Santé Universelle lancée" },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-xl border border-[#D4AF37]/25 bg-white/[0.05] backdrop-blur p-5 hover:border-[#D4AF37]/60 hover:bg-white/[0.08] transition-all"
                data-testid={`bilan-stat-${i}`}
              >
                <div className="font-display text-2xl md:text-3xl text-[#D4AF37] font-bold">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-white/75 mt-2 leading-snug">{s.l}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="bilan-axes">
            {[
              {
                icon: "🎓",
                title: "Éducation",
                text: "Gratuité de l'enseignement primaire effective depuis 2019. Des millions d'enfants congolais retournent à l'école — une révolution silencieuse qui change le destin d'une génération.",
              },
              {
                icon: "🏥",
                title: "Santé",
                text: "Lancement officiel de la Couverture Santé Universelle. Gratuité de la maternité, programme Mwinda contre la mortalité maternelle et infantile : la santé devient un droit pour tous.",
              },
              {
                icon: "🛣️",
                title: "Infrastructures",
                text: "Sauts-de-mouton à Kinshasa, modernisation des routes nationales, réhabilitation des aéroports, électrification rurale. Le Congo bétonne son avenir.",
              },
              {
                icon: "🌍",
                title: "Diplomatie",
                text: "Présidence de l'Union Africaine en 2021, adhésion à la Communauté Est-Africaine en 2022, leadership dans les processus de Luanda et Nairobi : la RDC retrouve sa place sur la scène internationale.",
              },
              {
                icon: "🛡️",
                title: "Sécurité & Paix",
                text: "Mobilisation diplomatique sans précédent face à l'agression à l'Est. Soutien indéfectible aux FARDC, défense de l'intégrité territoriale, recherche active de la paix régionale.",
              },
              {
                icon: "💼",
                title: "Économie & Développement",
                text: "PDL-145T : développement local des 145 territoires. Stabilité macroéconomique, croissance soutenue, soutien à l'entrepreneuriat des jeunes, Caisse Nationale de Péréquation.",
              },
            ].map((axe, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur p-6 hover:border-[#D4AF37]/50 transition-all"
                data-testid={`bilan-axe-${i}`}
              >
                <div className="text-3xl mb-3">{axe.icon}</div>
                <div className="font-display text-xl text-[#D4AF37] mb-2">{axe.title}</div>
                <p className="text-sm text-white/80 leading-relaxed">{axe.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12" data-testid="bilan-chantiers">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative rounded-xl overflow-hidden aspect-[16/10] group"
              data-testid="bilan-chantier-1"
            >
              <img src={PRESIDENT_PHOTOS.chantierRoute} alt="Construction de routes nationales en RDC" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44] via-[#0A1F44]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-1">Programme Routes 2025</div>
                <div className="font-display text-lg md:text-xl text-white">Désenclavement national en cours</div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative rounded-xl overflow-hidden aspect-[16/10] group"
              data-testid="bilan-chantier-2"
            >
              <img src={PRESIDENT_PHOTOS.chantierRdc} alt="Chantiers urbains à Kinshasa et provinces" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44] via-[#0A1F44]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-1">Modernisation urbaine</div>
                <div className="font-display text-lg md:text-xl text-white">Kinshasa et chefs-lieux en transformation</div>
              </div>
            </motion.div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 p-6 md:p-8 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <div className="flex items-start gap-4">
              <div className="text-5xl text-[#D4AF37] leading-none font-display">"</div>
              <p className="text-white/90 italic text-base md:text-lg leading-relaxed max-w-3xl">
                Le peuple congolais m'a confié un mandat sacré : reconstruire la République, restaurer la dignité du Congolais et redonner espoir à notre jeunesse. Nous y parviendrons ensemble.
                <span className="block mt-2 not-italic text-sm text-[#D4AF37] font-semibold tracking-wider">— Son Excellence Félix-Antoine Tshisekedi Tshilombo</span>
              </p>
            </div>
            <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A1F44] rounded-sm whitespace-nowrap font-bold" data-testid="bilan-cta">
              <Link to="/actions">Voir tous les axes <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ACTUALITÉS — CŒUR DE LA PAGE */}
      <section className="py-20 md:py-24 bg-[#FAFAFA]" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Actualités</div>
              <h2 className="font-display text-3xl md:text-5xl text-[#0A0F1A] mt-2 leading-tight">L'actualité du mouvement</h2>
              <p className="text-[#475569] mt-3 max-w-2xl">Suivez les rencontres, audiences, actions et mobilisations des A.F.B. à travers le pays et la diaspora.</p>
            </div>
            <Button asChild variant="outline" className="border-[#0052CC]/30 text-[#0052CC] hover:bg-[#0052CC]/5 rounded-sm" data-testid="news-view-all">
              <Link to="/actualites">Toutes les actualités <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
          </div>

          {news.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Featured */}
              <Link
                to={`/actualites/${news[0].id}`}
                className="lg:col-span-2 group relative rounded-2xl overflow-hidden bg-[#0A0F1A] block"
                data-testid="news-featured"
              >
                <div className="aspect-[16/10] lg:aspect-[16/11]">
                  <img src={news[0].image_url} alt={news[0].title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-[#0A0F1A]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7 md:p-10 text-white">
                  <div className="flex items-center gap-3 text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">
                    <span className="bg-[#CE1126] text-white px-2.5 py-1 rounded-sm">À la une</span>
                    <span>{news[0].category}</span>
                    <span className="text-white/50">•</span>
                    <span className="text-white/70 flex items-center gap-1.5"><Calendar size={12} /> {new Date(news[0].published_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-4xl leading-tight max-w-3xl group-hover:text-[#D4AF37] transition-colors">{news[0].title}</h3>
                  <p className="text-sm md:text-base text-white/80 mt-3 max-w-2xl line-clamp-2">{news[0].summary}</p>
                  <div className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold mt-5 group-hover:gap-3 transition-all">
                    Lire l'article <ArrowRight size={16} />
                  </div>
                </div>
              </Link>

              {/* Side cards */}
              <div className="space-y-6">
                {news.slice(1, 3).map((n, i) => (
                  <Link key={n.id} to={`/actualites/${n.id}`} data-testid={`news-side-${i}`} className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-lg transition-all flex">
                    <div className="w-1/3 shrink-0 overflow-hidden">
                      <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="w-2/3 p-5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">{n.category}</div>
                      <h3 className="font-display text-base md:text-lg text-[#0A0F1A] mt-1.5 leading-snug line-clamp-3 group-hover:text-[#0052CC] transition-colors">{n.title}</h3>
                      <div className="text-[11px] text-[#475569] mt-2 flex items-center gap-1.5">
                        <Calendar size={11} /> {new Date(n.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Row 2 */}
          {news.length > 3 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              {news.slice(3, 6).map((n, i) => (
                <Link key={n.id} to={`/actualites/${n.id}`} data-testid={`news-grid-${i}`} className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                      <span>{n.category}</span>
                      <span className="text-black/30">•</span>
                      <span className="text-black/40">{new Date(n.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                    </div>
                    <h3 className="font-display text-lg text-[#0A0F1A] mt-2 leading-tight line-clamp-2 group-hover:text-[#0052CC] transition-colors">{n.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ACTIONS DU PRÉSIDENT */}
      <section className="py-20 md:py-24" data-testid="actions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Bilan & Vision</div>
              <h2 className="font-display text-3xl md:text-5xl text-[#0A0F1A] mt-2">Actions du Président</h2>
              <p className="text-[#475569] mt-3 max-w-2xl">Six axes structurants qui transforment durablement la RDC.</p>
            </div>
            <Button asChild variant="outline" className="border-[#0052CC]/30 text-[#0052CC] hover:bg-[#0052CC]/5 rounded-sm" data-testid="actions-view-all">
              <Link to="/actions">Voir toutes les actions <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {actions.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group rounded-xl overflow-hidden bg-white border border-black/5 shadow-sm hover:shadow-xl transition-all"
                data-testid={`action-card-${i}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={a.image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">{a.domain}</div>
                  <h3 className="font-display text-xl text-[#0A0F1A] mt-1.5">{a.title}</h3>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">{a.description}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-[#0052CC]">
                    <Star size={12} fill="#D4AF37" stroke="#D4AF37" /> {a.impact}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* SOUTIEN À L'EST — images réelles de la guerre */}
      <section className="relative py-24 md:py-32 bg-[#0A0F1A] text-white overflow-hidden" data-testid="east-support-section">
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: `url(${PRESIDENT_PHOTOS.guerreExode})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1A] via-[#0A0F1A]/90 to-[#0A0F1A]/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-[#CE1126]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">Soutien à l'Est du Congo</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl leading-tight">
              Une mobilisation patriotique <br /> pour <span className="text-[#D4AF37]">la paix</span> et <span className="text-[#D4AF37]">la dignité</span>.
            </h2>
            <p className="text-white/85 mt-6 text-base md:text-lg leading-relaxed max-w-xl">
              Sous l'impulsion du Président Félix Tshisekedi, la RDC mène une diplomatie ferme et active pour la paix à l'Est : processus de Luanda et de Nairobi, soutien indéfectible aux FARDC, mobilisation de la communauté internationale contre l'agression. Les A.F.B. accompagnent cette mobilisation patriotique et soutiennent les populations déplacées.
            </p>
            <div className="flex gap-3 mt-8 flex-wrap">
              <Button asChild size="lg" className="bg-[#CE1126] hover:bg-[#A60E1F] text-white rounded-sm" data-testid="east-cta">
                <Link to="/soutien-est">Découvrir <ArrowRight size={16} className="ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0F1A] rounded-sm">
                <Link to="/dons">Soutenir l'Est</Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-10" data-testid="east-stats">
              {[
                { v: "+1.2M", l: "Personnes déplacées aidées" },
                { v: "145", l: "Territoires couverts" },
                { v: "24/7", l: "Mobilisation continue" },
                { v: "100%", l: "Transparence" },
              ].map((s, i) => (
                <div key={i} className="rounded-xl border border-white/15 bg-white/[0.06] backdrop-blur p-4" data-testid={`east-stat-${i}`}>
                  <div className="font-display text-2xl md:text-3xl text-[#D4AF37]">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mt-1.5">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Galerie photos réelles guerre */}
          <div className="grid grid-cols-2 gap-3" data-testid="east-gallery">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="col-span-2 relative rounded-xl overflow-hidden aspect-[16/10] group"
              data-testid="east-img-main"
            >
              <img src={PRESIDENT_PHOTOS.guerreFlee} alt="Déplacée fuyant les combats à l'Est de la RDC" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#CE1126] font-bold mb-1">Réalité du terrain</div>
                <div className="font-display text-lg md:text-xl">Des millions de civils sur les routes de l'exil</div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative rounded-xl overflow-hidden aspect-square group"
              data-testid="east-img-camp"
            >
              <img src={PRESIDENT_PHOTOS.guerreCamp} alt="Camp de déplacés à Goma" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A]/90 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">Camps de déplacés</div>
                <div className="text-xs text-white/85 mt-0.5">Conditions de vie précaires</div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative rounded-xl overflow-hidden aspect-square group"
              data-testid="east-img-exode"
            >
              <img src={PRESIDENT_PHOTOS.guerreExode} alt="Exode des populations" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A]/90 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">Exode forcé</div>
                <div className="text-xs text-white/85 mt-0.5">Goma, Nord-Kivu, Ituri</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES SECONDAIRES */}
      <section className="py-20 md:py-24 stars-pattern" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Services & Outils</div>
            <h2 className="font-display text-3xl md:text-5xl text-[#0A0F1A] mt-3 leading-tight">Une plateforme structurée et engagée</h2>
            <p className="text-[#475569] mt-4">Tous les outils nécessaires pour soutenir, participer et faire grandir le mouvement.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={s.testid}
              >
                <Link to={s.to} className="block bg-white border border-[#0052CC]/10 hover:border-[#D4AF37]/50 rounded-lg p-7 shadow-sm hover:shadow-xl transition-all h-full">
                  <div className="w-11 h-11 rounded-md bg-[#0052CC]/10 text-[#0052CC] flex items-center justify-center mb-4">
                    <s.icon size={20} />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#0A0F1A]">{s.title}</h3>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.text}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-24 bg-gradient-to-br from-[#0052CC] to-[#0A0F1A] text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 stars-pattern opacity-20" />
        <div className="relative max-w-4xl mx-auto text-center px-6">
          <Logo variant="badge" size={88} />
          <FourStars size={16} className="justify-center mt-6 mb-5" />
          <h2 className="font-display text-3xl md:text-5xl leading-tight">
            Devenez Ami de Fatshi Béton.
          </h2>
          <div className="text-sm md:text-base font-display tracking-[0.18em] text-[#D4AF37] mt-4">
            ★ UNITÉ — PROGRÈS — TRAVAIL ★
          </div>
          <p className="text-white/80 mt-5 text-lg leading-relaxed">
            Rejoignez un mouvement national, structuré et engagé au service du peuple congolais.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-9">
            <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A0F1A] rounded-sm px-8 py-6 text-base font-bold btn-lift" data-testid="cta-join-btn">
              <Link to="/register"><Users className="mr-2" size={18} /> Devenir membre</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[#0052CC] rounded-sm px-8 py-6 text-base">
              <Link to="/dons"><Heart className="mr-2" size={18} /> Faire un don</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
